const API_BASE =
  import.meta.env.DEV
    ? '/api/v1'
    : 'https://plangrab-api.frankpedersen84.workers.dev/api/v1';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

function handleAuthFailure(path: string) {
  if (path === '/auth/me' || path === '/auth/login') return;
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/login') return;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.replace(`/login?next=${next}`);
}

type ReqInit = RequestInit & { timeoutMs?: number };

// audit f29 (2026-06-18): every request runs under an AbortController timeout.
// fetch() had no timeout, so a hung Worker (a stalled Browser Rendering /
// Firecrawl deep-dive) left the promise unsettled forever — the UI spun on
// "Loading…" with no error and react-query never retried. A timeout turns "dead"
// into a real ApiError the page can surface.
async function rawFetch(path: string, init?: ReqInit): Promise<Response> {
  const ctrl = new AbortController();
  const timeoutMs = init?.timeoutMs ?? 20_000;
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      ...init,
      signal: ctrl.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') {
      throw new ApiError(0, 'timeout',
        `Request timed out after ${Math.round(timeoutMs / 1000)}s — the server may be busy. Try again.`);
    }
    throw new ApiError(0, 'network_error', (e as Error)?.message || 'Network error — check your connection.');
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) {
    let code = 'unknown';
    let message: string | undefined;
    // Read the body ONCE as text, then try to parse it as JSON. (Calling
    // res.text() after res.json() throws "body already read", so we can't read
    // twice — read text first.)
    const raw = await res.text().catch(() => '');
    try {
      const data = JSON.parse(raw) as {
        error?: unknown; message?: string; success?: boolean;
      };
      // Plain { error: string } from app handlers.
      if (typeof data.error === 'string') {
        code = data.error;
      }
      // Zod-validator returns { success: false, error: { issues: [...] } }.
      // Surface the issue messages so the page can render them.
      else if (data.error && typeof data.error === 'object' && 'issues' in (data.error as object)) {
        const issues = (data.error as { issues?: Array<{ path?: string[]; message?: string }> }).issues ?? [];
        code = 'validation_error';
        message = issues.map((i) =>
          `${(i.path ?? []).join('.') || 'field'}: ${i.message ?? 'invalid'}`,
        ).join(' · ');
      }
      if (data.message && !message) message = data.message;
    } catch {
      // Non-JSON error body (a 500 HTML page, a gateway error, an empty body).
      // Don't swallow it: surface the text / statusText so the ApiError.message
      // is a real diagnostic instead of an empty 'unknown'.
      code = `http_${res.status}`;
      message = raw.trim().slice(0, 200) || res.statusText || undefined;
    }
    if (res.status === 401) handleAuthFailure(path);
    throw new ApiError(res.status, code, message);
  }
  return res;
}

async function request<T>(path: string, init?: ReqInit): Promise<T> {
  const res = await rawFetch(path, init);
  return res.json() as Promise<T>;
}

// audit f24 (2026-06-18): drive attachment / ZIP downloads through the
// authenticated fetch wrapper (not a bare <a href>), so an expired session
// redirects to /login via handleAuthFailure instead of navigating the operator
// to a raw JSON 401 at the exact moment the document package matters.
async function downloadFile(path: string, filename: string): Promise<{ bytes: number }> {
  const res = await rawFetch(path, { method: 'GET', timeoutMs: 60_000 });
  const blob = await res.blob();
  // An empty body is a real failure (R2 object gone / 0-byte stream) — surface
  // it instead of "saving" a 0-byte file that looks like nothing happened.
  if (blob.size === 0) throw new ApiError(res.status, 'empty_file', 'The file came back empty (0 bytes) — re-run the document fetch for this opportunity.');
  if (typeof window === 'undefined') return { bytes: blob.size };
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Removing the anchor synchronously right after click() can cancel a blob
  // download in Chromium — keep it (and the object URL) alive briefly so the
  // browser commits the download. (Fix for "click does nothing, no download".)
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 5000);
  return { bytes: blob.size };
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  downloadFile,
};

export { API_BASE };
